import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { ActivityFeed, ControlEvent, ImpactData, OwnerControls } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading, NotMeasured, PageBody, PageHead,
  Panel, PanelHead, RouteMark, Stat, Tabs, Toggle,
} from '../../components/aa';
import { relativeTime } from '../partner/format';
import {
  CapabilityEventDrawer, EvaluationDrawer, RouteContractDrawer, activityToHistory, controlEventToHistory, sentLine,
} from './ControlDrawers';
import type { EvaluationRow, HistoryItem, RouteRow } from './ControlDrawers';

/*
 * AI capability management: approved routes, representative evaluations,
 * controlled rollouts and fallback coverage, read at the business level.
 *
 * Every number here has a source: the managed pool and its routes, the
 * company's registered model, reviewed runs on published capabilities, and the
 * recorded control events. Where the platform does not measure something yet
 * (quality deltas, release floors, rollout traffic) the page says "not measured
 * yet" rather than showing a figure nobody stands behind. Running an evaluation
 * or moving a rollout is done by Agents Anywhere; the Owner records the request.
 */

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'evaluations', label: 'Evaluations' },
  { id: 'rollouts', label: 'Rollouts' },
  { id: 'routes', label: 'Approved routes' },
  { id: 'history', label: 'History' },
];

const STEPS = [
  { title: 'Discover', desc: 'Candidate identified' },
  { title: 'Evaluate', desc: 'Representative cases' },
  { title: 'Shadow', desc: 'No user-facing change' },
  { title: 'Approve', desc: 'Named owners' },
  { title: 'Roll out', desc: '10% → 25% → 100%' },
  { title: 'Learn', desc: 'Results retained' },
];

const STAGES = [
  { key: 'baseline', label: 'Approved baseline', desc: 'Always available as fallback', mark: 'check' },
  { key: 'low', label: 'Low-risk work', desc: '10% eligible traffic', mark: '10' },
  { key: 'shadow', label: 'Broader shadow cohort', desc: '25% eligible traffic', mark: '25' },
  { key: 'full', label: 'Approved route', desc: '100% eligible traffic', mark: '100' },
];

const percent = (rate: number | null): React.ReactNode => {
  if (rate === null) return <NotMeasured />;
  return `${Math.round(rate * 100)}%`;
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`px-6 py-3.5 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft ${className}`}>{children}</th>
);

const AiCapabilitiesPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'overview';
  const setTab = (id: string) => setParams({ tab: id }, { replace: true });
  const clientName = localStorage.getItem('client_name') || 'Your company';

  const controls = useAsync<OwnerControls>(() => aaiApi.getOwnerControls(), 'Could not load AI capabilities.');
  const impact = useAsync<ImpactData>(() => aaiApi.getImpact(), 'Could not load reviewed runs.');
  const activity = useAsync<ActivityFeed>(() => aaiApi.getActivity('company', 50, { category: 'access' }), 'Could not load history.');

  const [route, setRoute] = useState<RouteRow | null>(null);
  const [evaluation, setEvaluation] = useState<EvaluationRow | null>(null);
  const [event, setEvent] = useState<HistoryItem | null>(null);
  const [query, setQuery] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [loadedAt] = useState(() => new Date());

  if (controls.loading) return <PageBody><Loading label="Loading AI capabilities" /></PageBody>;
  if (controls.error || !controls.data) return <PageBody><ErrorNote message={controls.error || 'Something went wrong.'} /></PageBody>;

  const data = controls.data;
  const ai = data.ai;
  const routes: RouteRow[] = ai.managed.routes;
  const candidate = ai.registry?.current_model || null;
  const fallback = ai.managed.status === 'active' ? '100%' : 'Direct';
  const evaluations: EvaluationRow[] = (impact.data?.skills.top_skills || []).map((s) => ({
    name: s.name, department: s.department, adoption: s.adoption, success_rate: s.success_rate,
  }));
  const visibleRoutes = routes.filter((r) => !query.trim() || `${r.lane} ${r.alias}`.toLowerCase().includes(query.trim().toLowerCase()));
  const history: HistoryItem[] = [
    ...data.events.map(controlEventToHistory),
    ...(activity.data?.events || []).map(activityToHistory),
  ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  const stageIndex = 0;
  const evaluationRequested = data.events.find((e: ControlEvent) => e.kind === 'evaluation_request' && e.subject === 'representative_evaluation');

  const requestEvaluation = async () => {
    setRequesting(true);
    try {
      const res = await aaiApi.recordControlEvent({ kind: 'evaluation_request', subject: 'representative_evaluation', status: 'requested', payload: { sets: evaluations.length } });
      toast.success(`Evaluation requested. ${sentLine(res.data)}`);
      controls.reload();
    } catch {
      toast.error('Could not record the request.');
    } finally {
      setRequesting(false);
    }
  };

  // AI & models settings is parked, so its shortcut is too.
  // const openSettings = () => window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'ai-providers' } }));

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="AI capabilities"
        title="AI capability management"
        sub="Approved routes, representative evaluations, controlled rollouts, and fallback coverage."
        // actions={<Action variant="quiet" onClick={openSettings}>AI settings</Action>}
      />

      <Panel className="mb-5">
        <div className="flex flex-wrap items-center gap-4 px-6 py-4">
          <div className="min-w-[220px] flex-1">
            <Kicker className="mb-1">Protected</Kicker>
            <div className="text-[14px] font-semibold text-ink">Workflows · sources · permissions · history</div>
          </div>
          <span className="text-ink-soft">+</span>
          <div className="min-w-[220px] flex-1">
            <Kicker className="mb-1">Evaluated</Kicker>
            <div className="text-[14px] font-semibold text-ink">Quality · speed · cost · reliability</div>
          </div>
          <span className="text-ink-soft">=</span>
          <div className="min-w-[220px] flex-1 rounded-card bg-canvas-inset px-4 py-3">
            <Kicker className="mb-1">Release</Kicker>
            <div className="text-[14px] font-semibold text-ink">Evidence · approval · fallback</div>
          </div>
        </div>
      </Panel>

      <Tabs variant="pills" tabs={TABS} active={tab} onChange={setTab} label="AI capability view" />

      {tab === 'overview' && (
        <>
          <div className="mb-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Stat label="Approved capabilities" value={routes.length} evidence={`${data.connections.length} visible work connections`} />
            <Stat tone="quiet" label="Upgrade candidate" value={candidate ? 1 : <NotMeasured reason="None" />} evidence={candidate ? `${candidate} · ${ai.registry?.status}` : 'No candidate in evaluation'} />
            <Stat label="Fallback coverage" value={fallback} evidence="Last approved route retained" />
            <Stat label="Production change" value="0%" evidence={evaluationRequested ? 'Evaluation queued' : 'No rollout in progress'} />
          </div>

          <Panel className="mb-5">
            <div className="flex flex-wrap items-start justify-between gap-6 px-6 py-6">
              <div className="min-w-0 flex-1">
                <Kicker className="mb-1.5">Current candidate</Kicker>
                <div className="text-[22px] font-bold leading-[28px] tracking-[-0.01em] text-ink">{candidate || 'No candidate yet'}</div>
                <p className="m-0 mt-1.5 text-[13.5px] leading-[21px] text-ink-dim">
                  {candidate
                    ? `Registered for ${clientName}. Agents Anywhere runs the evaluation against representative cases before any rollout.`
                    : `Work for ${clientName} runs on the managed approved pool. A candidate appears here once Agents Anywhere registers a model for evaluation.`}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {['Quality', 'Speed', 'Cost', 'Boundary changes'].map((k) => (
                    <div key={k} className="rounded-card border border-line bg-white px-4 py-3">
                      <div className="text-[12px] text-ink-dim">{k}</div>
                      <div className="mt-1 text-[14px] font-semibold text-ink">{k === 'Boundary changes' ? 'None' : <NotMeasured />}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-[260px] shrink-0 rounded-card border border-line bg-canvas px-4 py-4">
                <Chip>{evaluationRequested ? 'Evaluation queued' : candidate ? 'Registered' : 'No candidate'}</Chip>
                <div className="mt-2 text-[12.5px] text-ink-dim">
                  {evaluationRequested ? `Requested ${relativeTime(evaluationRequested.created_at)}` : ai.registry?.last_trained_at ? relativeTime(ai.registry.last_trained_at) : 'Nothing to evaluate'}
                </div>
                <Action className="mt-3 w-full" disabled reason="Agents Anywhere issues the recommendation after the evaluation runs">Waiting for recommendation</Action>
              </div>
            </div>
          </Panel>

          <div className="mb-5 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            {STEPS.map((s, i) => {
              const done = candidate ? i === 0 : false;
              return (
                <div key={s.title} className="flex min-h-[150px] flex-col rounded-panel border border-line bg-white px-5 py-5">
                  <div className="flex items-start gap-3">
                    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-[12px] font-semibold ${done ? 'bg-ink text-white' : 'bg-canvas-inset text-ink'}`}>
                      {done ? <Check size={14} strokeWidth={2.5} /> : i + 1}
                    </span>
                    <div>
                      <div className="text-[15px] font-semibold text-ink">{s.title}</div>
                      <div className="mt-0.5 text-[12.5px] text-ink-dim">{s.desc}</div>
                    </div>
                  </div>
                  <span className="mt-auto pl-11 text-ink-soft">→</span>
                </div>
              );
            })}
          </div>

          <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <Kicker className="mb-1.5">Capability contracts</Kicker>
              <h2 className="m-0 text-[22px] font-bold leading-[28px] tracking-[-0.01em] text-ink">Approved outcomes</h2>
            </div>
            <label className="relative block w-full max-w-[360px]">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search capabilities or work" className="h-11 w-full rounded-act border border-line-strong bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:border-ink" />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {visibleRoutes.map((r) => (
              <Panel key={r.alias}>
                <ListRow
                  mark={<RouteMark glyph="C" size="lg" className="!bg-ink !text-white" />}
                  kicker={`Approved route · ${r.alias}`}
                  title={r.lane}
                  foot={`Fallback ${r.fallback}`}
                  onClick={() => setRoute(r)}
                  arrow
                />
              </Panel>
            ))}
          </div>
        </>
      )}

      {tab === 'evaluations' && (
        <Panel>
          <PanelHead
            kicker="Current candidate"
            title="Representative evaluation"
            desc={`Reviewed runs on published capabilities · run by Agents Anywhere on request · private content excluded${evaluationRequested ? ` · evaluation requested ${relativeTime(evaluationRequested.created_at)}` : ''}.`}
            action={<Action size="lg" onClick={requestEvaluation} disabled={requesting || Boolean(evaluationRequested)} reason={evaluationRequested ? 'An evaluation is already requested' : 'Recording'}>Run evaluation</Action>}
          />
          {impact.loading && <Loading label="Loading reviewed runs" />}
          {impact.error && <div className="px-6 py-5"><ErrorNote message={impact.error} /></div>}
          {!impact.loading && evaluations.length === 0 && <EmptyState title="No evaluation sets yet" reason="An evaluation set appears once a published capability has reviewed runs." />}
          {evaluations.length > 0 && (
            <div className="overflow-x-auto px-6 pb-6 pt-4">
              <table className="w-full border-collapse">
                <thead className="bg-canvas-inset">
                  <tr><Th>Evaluation set</Th><Th>Coverage</Th><Th>Candidate</Th><Th>Release floor</Th><Th className="w-10"> </Th></tr>
                </thead>
                <tbody>
                  {evaluations.map((row) => (
                    <tr key={`${row.name}-${row.department}`} className="cursor-pointer border-t border-line-soft hover:bg-canvas" onClick={() => setEvaluation(row)}>
                      <td className="px-6 py-4 text-[14px] text-ink">{row.name}</td>
                      <td className="px-6 py-4 text-[14px] text-ink">{row.adoption} people</td>
                      <td className="px-6 py-4 text-[14px] text-ink">{percent(row.success_rate)}</td>
                      <td className="px-6 py-4 text-[14px] text-ink-dim">Not set</td>
                      <td className="px-6 py-4 text-right text-ink-soft">→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === 'rollouts' && (
        <Panel>
          <div className="flex flex-wrap items-start justify-between gap-6 px-6 pt-6">
            <div>
              <Kicker className="mb-1.5">Controlled change</Kicker>
              <h2 className="m-0 text-[22px] font-bold leading-[28px] tracking-[-0.01em] text-ink">{candidate ? 'Evaluation pending' : 'No rollout in progress'}</h2>
              <p className="m-0 mt-1.5 text-[13.5px] text-ink-dim">Eligible work only · names, records, sources, permissions, pricing, and owners unchanged.</p>
            </div>
            <div className="font-sans text-[40px] font-semibold leading-none tabular-nums text-ink">0%</div>
          </div>
          <div className="grid gap-3 px-6 py-6 md:grid-cols-2 xl:grid-cols-4">
            {STAGES.map((s, i) => {
              const active = i <= stageIndex;
              return (
                <div key={s.key} className={`rounded-panel border border-line px-4 py-4 ${active ? 'bg-canvas' : 'bg-white opacity-60'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full font-sans text-[12px] font-semibold ${active ? 'bg-ink-muted text-white' : 'bg-canvas-inset text-ink-soft'}`}>
                      {s.mark === 'check' ? <Check size={16} strokeWidth={2.5} /> : s.mark}
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold text-ink">{s.label}</div>
                      <div className="text-[12px] text-ink-dim">{s.desc}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mx-6 mb-6 flex items-center justify-between gap-4 rounded-panel border border-line bg-canvas px-5 py-4">
            <div>
              <div className="text-[14px] font-semibold text-ink">Automatic fallback</div>
              <div className="text-[12.5px] text-ink-dim">Last approved route remains available.</div>
            </div>
            <Toggle checked onChange={() => undefined} disabled label="Automatic fallback" />
          </div>
        </Panel>
      )}

      {tab === 'routes' && (
        <Panel>
          <PanelHead kicker="Registry" title="Approved routes" desc="Capability, risk, data access, quality floor, latency, availability, and cost." />
          <div className="overflow-x-auto px-6 pb-6 pt-4">
            <table className="w-full border-collapse">
              <thead className="bg-canvas-inset">
                <tr><Th>Capability</Th><Th>Current route</Th><Th>Fallback</Th><Th>Last checked</Th><Th className="w-10"> </Th></tr>
              </thead>
              <tbody>
                {routes.map((r) => (
                  <tr key={r.alias} className="cursor-pointer border-t border-line-soft hover:bg-canvas" onClick={() => setRoute(r)}>
                    <td className="px-6 py-4">
                      <div className="text-[14px] font-semibold text-ink">{r.lane}</div>
                      <div className="text-[12px] text-ink-dim">{r.alias}</div>
                    </td>
                    <td className="px-6 py-4 text-[14px] text-ink">Managed approved pool</td>
                    <td className="px-6 py-4 text-[14px] text-ink">{ai.managed.status === 'active' ? 'Ready' : 'Direct'}</td>
                    <td className="px-6 py-4 text-[14px] text-ink">{loadedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="px-6 py-4 text-right text-ink-soft">→</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {tab === 'history' && (
        <Panel>
          <PanelHead kicker="Audit" title="Capability history" desc="Operational telemetry remains restricted. Approved policy and outcome changes are retained as trusted records." />
          {activity.loading && <Loading label="Loading history" />}
          {!activity.loading && history.length === 0 && <EmptyState title="Nothing recorded yet" reason="Control requests, reviews and access changes appear here as they happen." />}
          <Divided>
            {history.map((h) => (
              <ListRow
                key={h.id}
                mark={<RouteMark glyph="C" size="md" />}
                kicker={`${h.at ? relativeTime(h.at) : 'Undated'} · ${h.actor}`}
                title={h.title}
                onClick={() => setEvent(h)}
                arrow
              />
            ))}
          </Divided>
        </Panel>
      )}

      {route && <RouteContractDrawer route={route} ai={ai} onClose={() => setRoute(null)} />}
      {evaluation && <EvaluationDrawer row={evaluation} onClose={() => setEvaluation(null)} onRecorded={controls.reload} />}
      {event && <CapabilityEventDrawer item={event} onClose={() => setEvent(null)} />}
    </PageBody>
  );
};

export default AiCapabilitiesPage;
